/**
 * Offline Authentication Guard Component
 * Controls access to reading history based on offline authentication status
 * Requirements: 9.6, 9.7
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOfflineAuthStore, offlineAuthHelpers } from '../../store/offlineAuthStore';
import { useAuthStore } from '../../store/authStore';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Loading } from '../common/Loading';

interface OfflineAuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  fallbackPath?: string;
}

/**
 * Component that guards access to reading history in offline scenarios
 */
export const OfflineAuthGuard: React.FC<OfflineAuthGuardProps> = ({
  children,
  requireAuth = true,
  fallbackPath = '/login'
}) => {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  
  // Get auth states
  const { isAuthenticated, user } = useAuthStore();
  const { 
    isOnline, 
    hasValidOfflineToken, 
    offlineUserId, 
    offlineUserEmail,
    checkOfflineAuth,
    refreshOfflineTokenStatus
  } = useOfflineAuthStore();

  // Check authentication status on mount and when online status changes
  useEffect(() => {
    const performAuthCheck = async () => {
      setIsChecking(true);
      
      if (isOnline) {
        // Online: use regular auth check
        // The main auth store will handle this
        setIsChecking(false);
      } else {
        // Offline: check token validity
        checkOfflineAuth();
        setIsChecking(false);
      }
    };

    performAuthCheck();
  }, [isOnline, checkOfflineAuth]);

  // Refresh token status periodically when offline
  useEffect(() => {
    if (!isOnline && hasValidOfflineToken) {
      const interval = setInterval(() => {
        refreshOfflineTokenStatus();
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [isOnline, hasValidOfflineToken, refreshOfflineTokenStatus]);

  // Show loading while checking
  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading size="lg" />
      </div>
    );
  }

  // If auth is not required, always allow access
  if (!requireAuth) {
    return <>{children}</>;
  }

  // Online scenario: use regular authentication
  if (isOnline) {
    if (!isAuthenticated) {
      return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }
    return <>{children}</>;
  }

  // Offline scenario: check offline token validity
  if (!hasValidOfflineToken) {
    return <OfflineLoginRequired fallbackPath={fallbackPath} />;
  }

  // Valid offline token: allow access with offline indicator
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <OfflineAccessBanner 
        userEmail={offlineUserEmail} 
        userId={offlineUserId}
      />
      {children}
    </div>
  );
};

/**
 * Component shown when offline login is required
 */
const OfflineLoginRequired: React.FC<{ fallbackPath: string }> = ({ fallbackPath }) => {
  const { expiresAt } = offlineAuthHelpers.getTokenExpirationInfo();
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-yellow-600 dark:text-yellow-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" 
              />
            </svg>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Offline Access Unavailable
          </h2>
          
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <p>
              You're currently offline and don't have a valid authentication token 
              to access your reading history.
            </p>
            
            {expiresAt && (
              <p>
                Your previous session expired on {expiresAt.toLocaleDateString()} 
                at {expiresAt.toLocaleTimeString()}.
              </p>
            )}
            
            <p>
              Please connect to the internet and log in to access your readings.
            </p>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={() => window.location.reload()}
              variant="primary"
              className="w-full"
            >
              Check Connection & Retry
            </Button>
            
            <Button
              onClick={() => window.location.href = fallbackPath}
              variant="secondary"
              className="w-full"
            >
              Go to Login Page
            </Button>
          </div>
          
          <div className="text-xs text-gray-500 dark:text-gray-500">
            <p>💡 Tip: Log in while online to cache your reading history for offline access.</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

/**
 * Banner shown when accessing content with offline authentication
 */
const OfflineAccessBanner: React.FC<{ 
  userEmail: string | null; 
  userId: string | null;
}> = ({ userEmail, userId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { expiresAt, isExpiringSoon } = offlineAuthHelpers.getTokenExpirationInfo();
  
  return (
    <div className={`bg-blue-50 dark:bg-blue-900 border-b border-blue-200 dark:border-blue-700 ${
      isExpiringSoon ? 'bg-yellow-50 dark:bg-yellow-900 border-yellow-200 dark:border-yellow-700' : ''
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              isExpiringSoon ? 'bg-yellow-500' : 'bg-blue-500'
            }`} />
            
            <span className={`text-sm font-medium ${
              isExpiringSoon 
                ? 'text-yellow-800 dark:text-yellow-200' 
                : 'text-blue-800 dark:text-blue-200'
            }`}>
              {offlineAuthHelpers.getOfflineAuthStatusMessage()}
            </span>
            
            {userEmail && (
              <span className={`text-xs ${
                isExpiringSoon 
                  ? 'text-yellow-600 dark:text-yellow-300' 
                  : 'text-blue-600 dark:text-blue-300'
              }`}>
                ({userEmail})
              </span>
            )}
          </div>
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`text-xs ${
              isExpiringSoon 
                ? 'text-yellow-700 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-200' 
                : 'text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200'
            } transition-colors`}
          >
            {isExpanded ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
        
        {isExpanded && (
          <div className={`pb-3 text-xs space-y-1 ${
            isExpiringSoon 
              ? 'text-yellow-700 dark:text-yellow-300' 
              : 'text-blue-700 dark:text-blue-300'
          }`}>
            <p>
              <strong>Status:</strong> Viewing cached content offline
            </p>
            {userId && (
              <p>
                <strong>User ID:</strong> {userId}
              </p>
            )}
            {expiresAt && (
              <p>
                <strong>Access expires:</strong> {expiresAt.toLocaleString()}
              </p>
            )}
            <p>
              <strong>Note:</strong> Some features may be limited while offline. 
              Connect to the internet for full functionality.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflineAuthGuard;